import React, { FC, memo, useEffect, useMemo, useState } from "react";
import {
  Button,
  Typography,
  Popover,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  message,
} from "antd";
import { useNavigate } from "react-router-dom";
import {
  ChevronDown,
  Search,
  Bell,
  Plus,
  User,
  LogOut,
  Bot,
} from "lucide-react";

import { useUserStore } from "../../store/slices/user";
import { useGlobalSearch } from "../../hooks/api/global-search/hooks";
import { IGlobalSearchItem } from "../../types/api/global-search";
import { useJobCreate } from "../../hooks/api/jobs/hooks";
import { useClientList } from "../../hooks/api/clients/hooks";
import { useFieldWorkerList } from "../../hooks/api/field-workers/hooks";
import NotificationBell from "../../components/NotificationBell";
import InitialsAvatar from "../../components/ui/InitialsAvatar";


const { Text } = Typography;

interface TopProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
  showDrawer: () => void;
}

const STATUS_OPTS = [
  { value: "draft",       label: "Draft" },
  { value: "scheduled",   label: "Scheduled" },
  { value: "in_progress", label: "In Progress" },
  { value: "completed",   label: "Completed" },
  { value: "cancelled",   label: "Cancelled" },
];

const PRIORITY_OPTS = [
  { value: "low",    label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high",   label: "High" },
  { value: "urgent", label: "Urgent" },
];

const Top: FC<TopProps> = ({
  collapsed,
  setCollapsed,
  showDrawer,
}) => {
  const navigate = useNavigate();

 const { user, logout } = useUserStore();

  const [isModalOpen, setIsModalOpen] =
    useState(false);

  const [isJobModalOpen, setIsJobModalOpen] =
    useState(false);

  const [jobForm] = Form.useForm();

  const { data: clientRes } = useClientList({ page: 1, limit: 200 });
  const clientOptions = ((clientRes?.data as any[]) ?? []).map((c) => {
    const name = c.business_name || `${c.first_name ?? ""} ${c.last_name ?? ""}`.trim() || "—";
    const sub = c.email || c.phone || c.mobile || "";
    return { value: c.id, label: sub ? `${name} (${sub})` : name };
  });

  const { data: workerRes } = useFieldWorkerList({ page: 1, limit: 200 });
  const workerOptions = ((workerRes?.data as any[]) ?? []).map((w) => {
    const name = `${w.first_name ?? ""} ${w.last_name ?? ""}`.trim() || w.email || "—";
    return { value: w.id, label: w.employee_number ? `${name} (${w.employee_number})` : name };
  });

  const { mutate: createJob, isPending: creatingJob } = useJobCreate({
    onSuccess: () => {
      message.success("Job created.");
      setIsJobModalOpen(false);
      jobForm.resetFields();
    },
    onError: (err: any) =>
      message.error(err?.response?.data?.message ?? err?.message ?? "Failed to create job."),
  });

  function handleCreateJob(values: any) {
    createJob({
      ...values,
      scheduled_start: values.scheduled_start?.toISOString(),
      scheduled_end: values.scheduled_end?.toISOString(),
    });
  }

  const toggleCollapse = () =>
    setCollapsed(!collapsed);

  const handleLogoutClick = () => {
    setIsModalOpen(true);
  };

  const confirmLogout = () => {
    setIsModalOpen(false);
    logout();
  };

  const handleProfileClick = () =>
    navigate("/profile");

  const renderIconButton = (isDrawer: boolean) => (
    <Button
      type="text"
      className={`shadow-none border-none hover:!bg-transparent ${
        isDrawer
          ? "block sm:hidden"
          : "hidden sm:flex"
      }`}
      onClick={
        isDrawer ? showDrawer : toggleCollapse
      }
      icon={
        <img
          src="/icon/menu-toggle.svg"
          alt=""
          className="w-5 h-5"
        />
      }
    />
  );

  const popoverContent = (
    <div className="flex flex-col gap-1 w-[150px]">
      <button
        onClick={handleProfileClick}
        className="flex items-center gap-2 px-3 py-2 hover:bg-slate-100 rounded-lg text-sm"
      >
        <User size={14} />
        My Profile
      </button>

      <button
        onClick={handleLogoutClick}
        className="flex items-center gap-2 px-3 py-2 hover:bg-red-50 rounded-lg text-sm text-red-500"
      >
        <LogOut size={14} />
        Logout
      </button>
    </div>
  );

  const TYPE_COLORS: Record<string, string> = {
  client: "bg-blue-100 text-blue-700",
  job: "bg-green-100 text-green-700",
  purchase_order: "bg-purple-100 text-purple-700",
  invoice: "bg-orange-100 text-orange-700",
  user: "bg-pink-100 text-pink-700",
  quotes: "bg-teal-100 text-teal-700",
};
const [search, setSearch] = useState("");
const [debouncedSearch, setDebouncedSearch] = useState("");
const [open, setOpen] = useState(false);

const { data, isFetching } = useGlobalSearch(debouncedSearch);
  useEffect(() => {
  const timer = setTimeout(() => {
    setDebouncedSearch(search.trim());
  }, 400);

  return () => clearTimeout(timer);
}, [search]);

const results = useMemo(() => {
  if (!data) return [];

  return [
    ...data?.data.clients,
    ...data?.data.jobs,
    ...data?.data.purchase_orders,
    ...data?.data.invoices,
    ...data?.data.users,
    ...data?.data.quotes,
  ];
}, [data]);

const handleResultClick = (item: IGlobalSearchItem) => {
  setOpen(false);

  switch (item.type) {
    case "job":
      navigate(`/jobs?job_id=${item.id}`);
      break;

    case "client":
      navigate(`/clients?client=${item.id}`);
      break;

    case "invoice":
      navigate(`/invoices?invoice=${item.id}`);
      break;

    case "purchase_order":
      navigate(`/purchase-orders?purchase_order=${item.id}`);
      break;

    case "user":
      navigate(`/field-workers?user=${item.id}`);
      break;
    case "quotation":
      navigate(`/quotes?quote=${item.id}`);
      break;
    default:
      break;
  }
};

useEffect(() => {
  const handler = () => setOpen(false);
  window.addEventListener("click", handler);
  return () => window.removeEventListener("click", handler);
}, []);

  return (
    <>
      {/* TOPBAR */}
      <div className="sticky top-0 z-30 bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex items-center gap-4">
        
        {/* LEFT */}
        <div className="flex items-center gap-3 flex-1">
          {renderIconButton(false)}
          {renderIconButton(true)}

          {/* SEARCH */}
        <div className="relative hidden sm:block">
  <Search size={16} className="text-slate-400 absolute left-3 top-2.5" />

  <input
    type="text"
    value={search}
    onChange={(e) => {
      setSearch(e.target.value);
      setOpen(true);
    }}
    placeholder="Search jobs, clients, invoices…"
    className="pl-9 pr-4 py-2 text-sm bg-slate-100 rounded-lg border border-transparent w-52 lg:w-72 focus:outline-none focus:border-primary transition-colors"
  />

  {open && debouncedSearch.length > 1 && (
    <div className="absolute mt-2 w-80 max-w-[calc(100vw-2rem)] bg-white shadow-lg border rounded-lg z-50 max-h-96 overflow-auto">
      {isFetching ? (
        <div className="p-3 text-sm text-slate-500">Searching...</div>
      ) : results.length === 0 ? (
        <div className="p-3 text-sm text-slate-500">No results found</div>
      ) : (
        results.map((item) => (
          <div
            key={`${item.type}-${item.id}`}
            onClick={() => handleResultClick(item)}

            className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 cursor-pointer"
          >
            <div>
              <div className="text-sm font-medium text-slate-800">
                {item.title}
              </div>
              <div className="text-xs text-slate-500">
                {item.subtitle}
              </div>
            </div>

            <span
              className={`text-xs px-2 py-0.5 rounded-full ${TYPE_COLORS[item.type]}`}
            >
              {item.type}
            </span>
          </div>
        ))
      )}
    </div>
  )}
</div>
        </div>

        {/* NEW JOB */}
        <button
          onClick={() =>
            setIsJobModalOpen(true)
          }
          className="hidden sm:flex items-center gap-2 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors hover:opacity-90 bg-primary"
        >
          <Plus size={16} />
          New Job
        </button>

        {/* AI ASSISTANT */}
        <button
          onClick={() => window.dispatchEvent(new CustomEvent("openAiChat"))}
          title="AI Assistant"
          className="ai-assistant-btn flex items-center gap-1.5 rounded-[10px] px-3.5 py-[7px] text-white text-[13px] font-semibold border-none cursor-pointer"
        >
          <Bot size={16} />
          <span className="hidden md:inline">AI Assistant</span>
        </button>

        {/* NOTIFICATIONS */}
        <NotificationBell />

        {/* PROFILE */}
        <Popover
          content={popoverContent}
          trigger="click"
          placement="bottomRight"
        >
          <div className="flex items-center gap-2 cursor-pointer">
            <InitialsAvatar name={user?.name || "User"} size={32} />

            <div className="hidden md:flex flex-col leading-tight">
              <Text className="text-sm font-medium text-slate-800">
                { user?.name}
              </Text>

              <Text className="text-xs text-slate-500">
                {user?.role_name}
              </Text>
            </div>

            <ChevronDown
              size={16}
              className="hidden md:block text-slate-500"
            />
          </div>
        </Popover>
      </div>

      {/* CREATE JOB MODAL */}
      <Modal
        open={isJobModalOpen}
        onCancel={() => { setIsJobModalOpen(false); jobForm.resetFields(); }}
        title={<span className="text-base font-semibold">Create New Job</span>}
        centered
        width={580}
        footer={[
          <Button key="cancel" onClick={() => { setIsJobModalOpen(false); jobForm.resetFields(); }}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={creatingJob}
            onClick={() => jobForm.submit()}
          >
            Create Job
          </Button>,
        ]}
        destroyOnClose
      >
        <Form form={jobForm} layout="vertical" size="middle" className="pt-2" onFinish={handleCreateJob}>
          <Form.Item
            name="title"
            label="Job Title"
            rules={[{ required: true, message: "Title is required" }]}
          >
            <Input placeholder="e.g. Annual fire safety inspection" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item
              name="client_id"
              label="Client"
              rules={[{ required: true, message: "Client is required" }]}
            >
              <Select
                showSearch
                allowClear
                placeholder="Select client…"
                options={clientOptions}
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
            <Form.Item name="assigned_user_id" label="Assigned Worker">
              <Select
                showSearch
                allowClear
                placeholder="Assign worker…"
                options={workerOptions}
                filterOption={(input, opt) =>
                  ((opt?.label as string) ?? "").toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="status" label="Status" initialValue="draft">
              <Select options={STATUS_OPTS} />
            </Form.Item>
            <Form.Item name="priority" label="Priority" initialValue="normal">
              <Select options={PRIORITY_OPTS} />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="scheduled_start" label="Scheduled Start">
              <DatePicker showTime className="w-full" format="DD/MM/YYYY HH:mm" />
            </Form.Item>
            <Form.Item name="scheduled_end" label="Scheduled End">
              <DatePicker showTime className="w-full" format="DD/MM/YYYY HH:mm" />
            </Form.Item>
          </div>

          <div className="grid grid-cols-2 gap-x-4">
            <Form.Item name="estimated_value" label="Estimated Value (£)">
              <InputNumber className="w-full" placeholder="0.00" min={0} prefix="£" />
            </Form.Item>
            <Form.Item name="po_number" label="PO Number">
              <Input placeholder="Client PO reference…" />
            </Form.Item>
          </div>

          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Job description…" />
          </Form.Item>
        </Form>
      </Modal>

      {/* LOGOUT MODAL */}
      <Modal
        title="Logout Confirmation"
        open={isModalOpen}
        onOk={confirmLogout}
        onCancel={() =>
          setIsModalOpen(false)
        }
        okText="Yes, Logout"
        cancelText="Cancel"
      >
        <p>
          Are you sure you want to logout?
        </p>
      </Modal>
    </>
  );
};

export default memo(Top);